// @ts-nocheck
import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Building, ChevronsUpDown, Check, X } from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { useWorkspace } from '@/contexts/context';
import { toast } from 'sonner';

interface ClientSelectorProps {
  selectedClientIds: string[];
  onSelectionChange: (clientIds: string[]) => void;
}

export function ClientSelector({ selectedClientIds, onSelectionChange }: ClientSelectorProps) {
  const { workspace } = useWorkspace();
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (workspace) {
      loadClients();
    }
  }, [workspace]);

  const loadClients = async () => {
    if (!workspace) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('clients')
        .select('id, name, status, arr')
        .eq('workspace_id', workspace.id)
        .order('name');

      if (error) throw error;
      setClients(data || []);
    } catch (error: any) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleClient = (clientId: string) => {
    if (selectedClientIds.includes(clientId)) {
      onSelectionChange(selectedClientIds.filter(id => id !== clientId));
    } else {
      onSelectionChange([...selectedClientIds, clientId]);
    }
  };

  const removeClient = (clientId: string) => {
    onSelectionChange(selectedClientIds.filter(id => id !== clientId));
  };

  const selectedClients = clients.filter(c => selectedClientIds.includes(c.id));

  return (
    <div className="space-y-2">
      <Label>Linked Clients</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4" />
              {selectedClientIds.length > 0
                ? `${selectedClientIds.length} client${selectedClientIds.length > 1 ? 's' : ''} selected`
                : 'Select clients...'}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput placeholder="Search clients..." />
            <CommandEmpty>No clients found.</CommandEmpty>
            <CommandGroup>
              <ScrollArea className="h-[200px]">
                {clients.map((client) => (
                  <CommandItem
                    key={client.id}
                    value={client.name}
                    onSelect={() => toggleClient(client.id)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <div className={`h-4 w-4 border rounded ${
                          selectedClientIds.includes(client.id)
                            ? 'bg-[#245F73] border-[#245F73]'
                            : 'border-muted-foreground'
                        } flex items-center justify-center`}>
                          {selectedClientIds.includes(client.id) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <span>{client.name}</span>
                      </div>
                      {client.arr && (
                        <span className="text-xs text-muted-foreground">
                          ${client.arr.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </ScrollArea>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Clients */}
      {selectedClients.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedClients.map((client) => (
            <Badge key={client.id} variant="outline" className="pl-3 pr-1">
              <span className="mr-1">{client.name}</span>
              <button
                onClick={() => removeClient(client.id)}
                className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
